/**
 * 내부 전용 zod 스키마 introspection 유틸.
 * extractSearchParams / buildFilterSearchParams 양쪽에서 공유한다
 * (읽기/쓰기가 같은 "options 화이트리스트 + 타입" 판단 기준을 써야 포맷이 안 벌어진다).
 */
import { z } from "zod";

/**
 * ZodDefault/ZodOptional/ZodNullable 래핑만 벗겨서 안의 스키마를 꺼낸다.
 * ZodArray도 자체적으로 .unwrap()(원소 타입 반환)을 갖고 있어서, 벗길 대상을
 * Default/Optional/Nullable로 명시적으로 한정한다 (배열까지 벗겨지는 것을 방지).
 */
export function unwrap(schema: unknown): unknown {
    let current = schema;
    while (
        current instanceof z.ZodDefault ||
        current instanceof z.ZodOptional ||
        current instanceof z.ZodNullable
    ) {
        current =
            current instanceof z.ZodDefault
                ? current.removeDefault()
                : current.unwrap();
    }
    return current;
}

/** 값 하나를 필드 스키마 타입에 맞게 캐스팅한다. 숫자 계열이면 Number로, 그 외엔 문자열 그대로. */
export function castValue(raw: string, valueSchema: unknown): unknown {
    const base = unwrap(valueSchema);
    if (base instanceof z.ZodNumber) {
        const n = Number(raw);
        return Number.isNaN(n) ? raw : n;
    }
    return raw;
}

/** 스키마의 options 필드(z.object(...))의 shape(키별 스키마)를 읽어온다. */
export function getOptionsShape(schema: z.ZodObject<z.ZodRawShape>): z.ZodRawShape {
    const optionsField = schema.shape.options;
    if (!optionsField) return {};
    const unwrapped = unwrap(optionsField);
    if (!(unwrapped instanceof z.ZodObject)) return {};
    return unwrapped.shape;
}
